<template>
  <div class="doc-upload-wrapper w-100 position-relative">
    <div
      class="d-flex align-items-center"
      :class="variant === 'admin' ? 'admin-doc-upload-label' : 'doc-upload-label'"
    >
      {{ label }}
      <component v-if="labelIcon" :is="labelIcon" class="ms-1" />
    </div>
    <input
      :id="id"
      ref="inputRef"
      class="d-none"
      type="file"
      accept=".pdf"
      @change="onChange"
      data-testid="input-fileInput"
    />
    <label
      :for="id"
      class="doc-upload-default d-flex align-items-center justify-content-center p-4"
      :class="variant === 'admin' ? 'admin-doc-upload' : 'doc-upload'"
      :style="[filePreview ? { background: 'rgba(28, 86, 145, 0.05)' } : {}]"
      @dragover.prevent
      @dragenter.prevent
      @drop="handleDrop"
    >
      <div
        class="d-flex flex-column align-items-center justify-content-center"
        v-if="!filePreview"
      >
        <PdfDocIcon />
        <p class="drag-file-wrapper">
          Drag file here to Upload <br />
          or <span style="color: var(--ch-blue-100);">Choose File</span>
        </p>
      </div>
      <div v-else>
        <div class="d-flex flex-column flex-lg-row align-items-center">
          <div style="max-width: 2rem;">
            <PdfPictureIcon />
          </div>
          <span class="file-preview ms-2">{{ filePreview }}</span>
        </div>
        <Button
          variant="naked"
          class="file-thrash-wrapper"
          @click="handleRemove"
          testId="remove-file-btn"
        >
          <div
            class="w-100 h-100 d-flex align-items-center justify-content-center"
          >
            <FileThrashIcon />
          </div>
        </Button>
      </div>
    </label>
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";

import { Button } from "@/components/global";
import { PdfDocIcon, PdfPictureIcon, FileThrashIcon } from "@/components/svg";

import { InputVariant } from "@/types";

const props = defineProps<{
  label?: string;
  labelIcon?: string | object | null;
  variant?: InputVariant;
  filePreview?: string;
  id?: string;
}>();

const emit = defineEmits<{
  (e: 'change', files: FileList): void;
  (e: 'removeFile'): void;
}>();

const inputRef = ref<HTMLInputElement | null>(null);

const onChange = (e: Event) => {
  const files = (e.target as HTMLInputElement).files;
  if (files) {
    emit('change', files);
  }

  (e.target as HTMLInputElement).value = '';
};

const handleRemove = () => {
  emit('removeFile');
};

const handleDrop = (e: DragEvent) => {
  const files = e.dataTransfer?.files;

  if (files && files[0]?.type === "application/pdf") {
    emit('change', files);

    if (inputRef.value) inputRef.value.value = '';
  }
};

defineExpose({ inputRef });
</script>

<style scoped>
.doc-upload-wrapper .doc-upload-default {
  font-size: var(--font-xs);
  font-weight: 400;
  width: 100%;
  height: 8rem;
  padding: 0.5rem;
}

.doc-upload-wrapper .doc-upload-label {
  color: #000000;
  font-size: var(--font-xs);
  font-weight: 400;
  margin-bottom: 0.25rem;
  text-transform: capitalize;
}

.doc-upload-wrapper .doc-upload {
  background: transparent;
  border: 1px solid var(--ch-blue-100);
  color: #000000;
  border-radius: 6px;
}

.doc-upload-wrapper .admin-doc-upload-label {
  color: var(--ch-text-head);
  font-size: var(--font-xs);
  font-weight: 400;
  margin-bottom: 0.2rem;
  text-transform: capitalize;
}

.doc-upload-wrapper .admin-doc-upload {
  background: none;
  border: 1px solid var(--ch-input);
  color: var(--ch-text-head);
  border-radius: 5px;
}

div .drag-file-wrapper {
  color: rgba(0, 0, 0, 0.32);
  font-size: 1rem;
  font-weight: 400;
  text-align: center;
  margin: 0.5rem 0 0 0;
  line-height: 18px;
  letter-spacing: -0.01em;
  opacity: 0.54;
}

div .file-preview {
  width: 132px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: rgba(0, 0, 0, 0.4);
  font-size: var(--font-sm);
}

div .file-thrash-wrapper {
  position: absolute;
  bottom: 0.75rem;
  right: 0.75rem;
  width: 30px;
  height: 30px;
  border-radius: 100%;
  background-color: rgba(28, 86, 145, 0.3);
}

@media screen and (min-width: 1024px) {
  .doc-upload-wrapper .doc-upload-label {
    font-size: var(--font-sm);
  }
}
</style>
